import {
    AlignLeft,
    AtSign,
    BadgeCheck,
    Braces,
    Brain,
    Building2,
    Calendar,
    CalendarCheck,
    CalendarClock,
    CircleCheck,
    Clock,
    Flag,
    Gauge,
    Hash,
    Home,
    Import,
    Link,
    List,
    Mail,
    MapPin,
    Megaphone,
    MessageSquare,
    MessagesSquare,
    Phone,
    Repeat,
    Send,
    Split,
    Tag,
    Target,
    ToggleLeft,
    TriangleAlert,
    Type,
    UserCheck,
    UserRound,
    UserX,
    Users,
    Variable,
    Workflow,
    Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { IVariableItem, VariableValueType } from './types';

/**
 * The glyph for ONE variable, as opposed to the glyph for its whole category
 * (`variable-icons.tsx`).
 *
 * Resolution is deliberately name-first and type-second. The workflow node
 * catalog declares its outputs as `{ path, label }` with no type at all (every
 * `*.executor.ts` under `backend/.../workflows/nodes/`), so for most workflow
 * variables the path IS the only signal there is.
 *
 * The resolver returns a KEY, not a component: a component type has to be
 * statically reachable for `react-hooks/static-components`, so the map lookup
 * belongs at the render site (`VariableItemIcon`), not behind a function call.
 */

/** A closed vocabulary of row glyphs, so two variables meaning the same thing look the same. */
export const ITEM_ICONS = {
    person: UserRound,
    personAssigned: UserCheck,
    personRemoved: UserX,
    audience: Users,
    email: Mail,
    phone: Phone,
    address: Home,
    business: Building2,
    place: MapPin,
    message: MessageSquare,
    conversation: MessagesSquare,
    sent: Send,
    text: Type,
    prose: AlignLeft,
    tag: Tag,
    campaign: Megaphone,
    list: List,
    stage: Flag,
    status: BadgeCheck,
    booking: CalendarCheck,
    meeting: CalendarClock,
    time: Clock,
    date: Calendar,
    link: Link,
    truth: CircleCheck,
    branch: Split,
    score: Gauge,
    thinking: Brain,
    structure: Braces,
    warning: TriangleAlert,
    goal: Target,
    loop: Repeat,
    count: Hash,
    toggle: ToggleLeft,
    engine: Workflow,
    origin: Import,
    event: Zap,
    custom: AtSign,
    unknown: Variable,
} as const satisfies Record<string, LucideIcon>;

export type VariableItemIconName = keyof typeof ITEM_ICONS;

/**
 * Keyed on the token's LAST dotted segment, normalised. Curated against the
 * real inventory: every `outputVariables` path the node catalog declares, the
 * twelve contact merge tags, and the engine-owned `trigger.*` paths.
 *
 * Entries are in normalised form — lower case, no underscores — so `first_name`,
 * `firstName` and `FIRST_NAME` all land here together.
 */
const BY_NAME: Readonly<Record<string, VariableItemIconName>> = {
    // people
    firstname: 'person',
    lastname: 'person',
    fullname: 'person',
    name: 'person',
    contact: 'person',
    contactname: 'person',
    actorname: 'person',
    inviteename: 'person',
    customerfirstname: 'person',
    repname: 'personAssigned',
    assignedto: 'personAssigned',
    isnewcontact: 'personAssigned',
    cancelledby: 'personRemoved',

    // reaching them
    email: 'email',
    phone: 'phone',
    channelidentity: 'phone',

    // where they are
    streetaddress: 'address',
    addressline2: 'address',
    fulladdress: 'address',
    city: 'place',
    state: 'place',
    country: 'place',
    postalcode: 'place',
    location: 'place',
    businessname: 'business',

    // message content
    body: 'message',
    renderedbody: 'message',
    renderedsubject: 'text',
    title: 'text',
    label: 'tag',
    summary: 'prose',
    reason: 'prose',
    outboundmessage: 'sent',
    unifiedconversation: 'conversation',

    // audience + classification
    segment: 'audience',
    tag: 'tag',
    tags: 'tag',
    tagnames: 'tag',
    campaign: 'campaign',
    sequence: 'list',
    poll: 'list',
    stage: 'stage',
    fromstage: 'stage',
    tostage: 'stage',
    outcome: 'stage',
    status: 'status',
    mode: 'branch',

    // scheduling
    booking: 'booking',
    oldbooking: 'booking',
    meetingtype: 'meeting',
    meetingtypename: 'meeting',
    startat: 'time',
    appointmenttime: 'time',
    scheduledfor: 'time',

    // links
    url: 'link',
    bookingurl: 'link',
    managelink: 'link',
    joinlink: 'link',
    joinlinkname: 'link',

    // AI + control flow
    decision: 'truth',
    matched: 'truth',
    met: 'truth',
    matchedbranch: 'branch',
    split: 'branch',
    confidence: 'score',
    reasoning: 'thinking',
    fields: 'structure',
    item: 'structure',
    missingrequired: 'warning',
    goalkey: 'goal',
    goal: 'goal',
    loop: 'loop',
    index: 'count',

    // contact-record columns the condition builder filters on
    isoptedout: 'personRemoved',
    createdat: 'date',
    lastcontactedat: 'time',

    // engine
    eventname: 'event',
    payload: 'structure',
    execution: 'count',
    provider: 'engine',
    source: 'origin',
    wasnewsubmission: 'toggle',
};

/** Last resort before the default: what the value IS, when the producer knows. */
const BY_TYPE: Readonly<Record<VariableValueType, VariableItemIconName>> = {
    string: 'text',
    number: 'count',
    boolean: 'toggle',
    date: 'date',
    object: 'structure',
    array: 'list',
};

/** Lower case, underscores dropped — `follow_up_date` and `followUpDate` agree. */
function normalize(segment: string): string {
    return segment.replace(/_/g, '').toLowerCase();
}

/**
 * The meaningful tail of a token: `steps.n_ab12.output.trigger.payload.tagIds`
 * and a bare `tag_ids` both reduce to `tagids`, then to `tag`.
 *
 * Two candidates, most specific first — the full segment, then the same with a
 * trailing `id`/`ids` removed, so an id column borrows its subject's glyph
 * (`campaignId` → `campaign` → Megaphone) without needing its own entry.
 */
function nameCandidates(token: string): readonly string[] {
    const segments = token.split('.').filter((part) => part.length > 0);
    const last = normalize(segments[segments.length - 1] ?? token);
    const stripped = last.replace(/ids?$/, '');
    return stripped.length > 0 && stripped !== last ? [last, stripped] : [last];
}

/**
 * The glyph a PATH earns on its name alone, or `undefined` when nothing matches.
 *
 * Exported for callers whose value types are not `VariableValueType` — the
 * condition builder's fields are widget types (`select`, `enum`, `tags`), so it
 * needs the name vocabulary first and then supplies its own fallback. Sharing
 * this is what makes a trigger's `trigger.payload.tagIds` condition wear the
 * same glyph as the `{{trigger.payload.tagIds}}` variable.
 */
export function variableIconNameForPath(token: string): VariableItemIconName | undefined {
    for (const candidate of nameCandidates(token)) {
        const byName = BY_NAME[candidate];
        if (byName) return byName;
    }
    return undefined;
}

/**
 * Which glyph a variable row shows. Never falls back to the CATEGORY's own
 * glyph: a step group whose every row repeated its heading would be worse than
 * no icon at all.
 */
export function resolveVariableItemIconName(item: IVariableItem): VariableItemIconName {
    const byName = variableIconNameForPath(item.token);
    if (byName) return byName;
    if (item.type) return BY_TYPE[item.type];
    // The `custom.field_name` placeholder is a stand-in for "a field you made",
    // which no glyph in the vocabulary describes better than the category's.
    return item.token.startsWith('custom.') ? 'custom' : 'unknown';
}
